/// <amd-module name="Wiki/Editor/ScrollHelper" />
import { TimerManagement } from "VSS/Core/TimerManagement";
import { shallowCompare } from "VSS/Core/Util/Object";
import { MarkdownConstants } from "VSS/Features/Markdown/MarkdownConstants";
import { LineAttrInfoMap } from "VSS/Features/Markdown/MarkdownRendererContracts";

// styles that affect the scroll height of the editor content.
interface TextAreaScrollStyles {
    height: string;
    width: string;
    "overflow-wrap": string;
    "white-space": string;
}

const THROTTLE_TIME_IN_MS = 2000;

export class ScrollHelper {
    private _previewToEditorScrollMapping: { [scrollDistance: string]: number };
    private _editorToPreviewScrollMapping: { [scrollDistance: string]: number };
    private _markdownLinesToEditorScrollMapping: { [line: number]: number };
    private _markdownLinesMapping: LineAttrInfoMap | undefined;

    private _editorHtmlElement: HTMLTextAreaElement;
    private _markdownContainerHtmlElement: HTMLDivElement;
    private _editorStyles: CSSStyleDeclaration;
    private _textAreaScrollStyles: TextAreaScrollStyles | undefined;

    private _isEditorScrollInProgress: boolean;
    private _isPreviewScrollInProgress: boolean;
    private _isUserTyping: boolean;
    private _areMappingsChanged: boolean;
    private _isSyncScrollEnabled: boolean;

    private _maxEditorScrollTop: number;
    private _isInitialized: boolean;

    private _throttledUpdatePreviewPosition: () => void;

    private _debounceCreateMappings: () => void;

    constructor(timerManagement?: TimerManagement) {// optional parameter required for UT's
        this._isUserTyping = false;
        this._isSyncScrollEnabled = true;
        this._resetMappings();
        timerManagement = timerManagement || new TimerManagement();
        this._throttledUpdatePreviewPosition = timerManagement.throttle(this._changePreviewPositionOnTyping, THROTTLE_TIME_IN_MS);
        this._debounceCreateMappings = timerManagement.debounce(this.createMappings, 1000);
        this._isInitialized = false;
    }

    public initialize(editorContainer: HTMLTextAreaElement, markdownContainer: HTMLDivElement) {
        this._isInitialized = true;
        this._editorHtmlElement = editorContainer;
        this._markdownContainerHtmlElement = markdownContainer;

        this._markdownContainerHtmlElement.addEventListener("scroll", this._onPreviewScroll);
        this._editorHtmlElement.addEventListener("scroll", this._onEditorScroll);
        this._editorHtmlElement.addEventListener("keypress", this._onKeyPress);
        this._editorHtmlElement.addEventListener("keyup", this._onKeyUp);

        this._editorStyles = window.getComputedStyle(this._editorHtmlElement as HTMLTextAreaElement);
    }

    public setSyncScroll(enableSyncScroll: boolean, editorContainer?: HTMLTextAreaElement, markdownContainer?: HTMLDivElement): void {
        this._isSyncScrollEnabled = enableSyncScroll;

        if (!this._isSyncScrollEnabled) {
            this._removeListeners();
        } else if (editorContainer && markdownContainer) {
            this.initialize(editorContainer, markdownContainer);
            this._areMappingsChanged = true;
        }
    }

    public onMarkdownLinesMappingChanged = (mapping: LineAttrInfoMap): void => {
        this._markdownLinesMapping = mapping;
        this._areMappingsChanged = true;
    };

    // public for UTs
    public createMapppingsIfNeeded = (): void => {
        if (!shallowCompare(this._getTextAreaStyles(), this._textAreaScrollStyles) || this._areMappingsChanged) {
            this._areMappingsChanged = false;
            this.createMappings();
        }
    };

    // public for UTs
    public createMappings = (): void => {
        if (!this._markdownLinesMapping || !this._editorHtmlElement || !this._markdownContainerHtmlElement) {
            return;
        }

        this._textAreaScrollStyles = this._getTextAreaStyles();

        this._createMarkdownLinesToEditorScrollMapping();

        const mappings = this._createPreviewScrollToEditorScrollRangeMapping();

        this._previewToEditorScrollMapping = {};
        this._editorToPreviewScrollMapping = {};

        mappings.forEach((value, key) => {
            const scrollDistance = (key.editorScrollEnd - key.editorScrollStart) / (value.previewScrollEnd - value.previewScrollStart);
            let j = key.editorScrollStart;
            for (let i = value.previewScrollStart; i <= value.previewScrollEnd; i++) {
                this._previewToEditorScrollMapping[i] = Math.round(j);
                j += scrollDistance;
            }
        });

        for (const scrollDistance in this._previewToEditorScrollMapping) {
            const value = this._previewToEditorScrollMapping[scrollDistance];

            if (this._editorToPreviewScrollMapping[value] === undefined) {
                this._editorToPreviewScrollMapping[value] = +scrollDistance;
            }
        }
    };

    private _resetMappings(): void {
        this._isInitialized = false;
        this._previewToEditorScrollMapping = {};
        this._editorToPreviewScrollMapping = {};
        this._markdownLinesToEditorScrollMapping = {};
    }

    private _removeListeners(): void {
        this._markdownContainerHtmlElement.removeEventListener("scroll", this._onPreviewScroll);
        this._editorHtmlElement.removeEventListener("scroll", this._onEditorScroll);
        this._editorHtmlElement.removeEventListener("keypress", this._onKeyPress);
        this._editorHtmlElement.removeEventListener("keyup", this._onKeyUp);
        this._resetMappings();
    }

    private _onKeyPress = (event: KeyboardEvent): void => {
        if (!this._isInitialized) {
            this.createMappings();
        } else if (event.key === "Enter") {
            // Not handling for backspace currently
            this._debounceCreateMappings();
        }
        this._isUserTyping = true;
        this._throttledUpdatePreviewPosition();
    };

    private _changePreviewPositionOnTyping = (): void => {
        const mdLineNumber = this._editorHtmlElement.value.substring(0, this._editorHtmlElement.selectionStart).split("\n").length;
        const editorScrollHeight = this._markdownLinesToEditorScrollMapping[mdLineNumber];

        if (this._editorToPreviewScrollMapping[editorScrollHeight] !== undefined) {
            this._isEditorScrollInProgress = true; // we don't want the preview scoll to act upon this
            this._markdownContainerHtmlElement.scrollTop =
                this._editorToPreviewScrollMapping[editorScrollHeight] - editorScrollHeight + this._editorHtmlElement.scrollTop; // to align the preview with editor on the same line
        }
    };

    private _onKeyUp = (): void => {
        this._isUserTyping = false;
    };

    private _onPreviewScroll = (): void => {
        // we don't want to act on preview scroll happening due to editor scroll event
        if (this._isEditorScrollInProgress) {
            this._isEditorScrollInProgress = false;
            return;
        }

        this._isPreviewScrollInProgress = true;
        this.createMapppingsIfNeeded();

        const mappedEditorPosition = this._getEditorScrollTop(this._markdownContainerHtmlElement.scrollTop);
        if (mappedEditorPosition !== undefined) {
            this._editorHtmlElement.scrollTop = mappedEditorPosition + 1; // this handles for the case when zoom level is not 100%
        }
    };

    private _onEditorScroll = (): void => {
        if (this._isPreviewScrollInProgress) {
            this._isPreviewScrollInProgress = false;
            return;
        }

        if (this._isUserTyping) {
            // we dont want to act on editor scroll event when user is typing this will be taken care by onKeyress Method
            return;
        }

        this._isEditorScrollInProgress = true;
        this.createMapppingsIfNeeded();

        const mappedPreviewPosition = this._getPreviewScrollTop(this._editorHtmlElement.scrollTop);
        if (mappedPreviewPosition !== undefined) {
            this._markdownContainerHtmlElement.scrollTop = mappedPreviewPosition + 1;
        }
    };

    private _getTextAreaStyles(): TextAreaScrollStyles {
        return {
            height: this._editorStyles.getPropertyValue("height"),
            width: this._editorStyles.getPropertyValue("width"),
            "overflow-wrap": this._editorStyles.getPropertyValue("overflow-wrap"),
            "white-space": this._editorStyles.getPropertyValue("white-space")
        };
    }

    private _getEditorScrollTop(scrollPosition: number): number | undefined {
        return this._previewToEditorScrollMapping[Math.ceil(scrollPosition)];
    }

    private _getPreviewScrollTop(scrollPosition: number): number | undefined {
        return this._editorToPreviewScrollMapping[Math.ceil(scrollPosition)];
    }

    private _createMarkdownLinesToEditorScrollMapping(): void {
        const markdownContent = this._editorHtmlElement.value.split("\n");
        const lineHeight = parseInt(this._editorStyles.getPropertyValue("line-height"));
        const paddingTop = parseInt(this._editorStyles.getPropertyValue("padding-top"));
        this._markdownLinesToEditorScrollMapping = {};
        const cummulativeEditorLinesCovered: number[] = [];

        const canvasElement = document.createElement("CANVAS") as HTMLCanvasElement;

        const canvasContext = canvasElement.getContext && canvasElement.getContext("2d");
        if (!canvasContext) {
            return;
        }
        canvasContext.font = "12px Menlo, Consolas, Courier New, monospace"; // should be same as editor textArea's font

        const textAreaWidth = this._editorHtmlElement.scrollWidth - 20; // scrollWidth is required in case when word wrap is disabled

        for (let i = 0; i < markdownContent.length; i++) {
            let linesCovered = Math.ceil(canvasContext.measureText(markdownContent[i]).width / textAreaWidth);

            // possible when there is a newline character
            if (linesCovered === 0) {
                linesCovered = 1;
            }

            if (i !== 0) {
                cummulativeEditorLinesCovered.push(cummulativeEditorLinesCovered[i - 1] + linesCovered);
            } else {
                cummulativeEditorLinesCovered.push(linesCovered);
            }
        }

        for (let i = 0; i < cummulativeEditorLinesCovered.length; i++) {
            if (i === 0) {
                this._markdownLinesToEditorScrollMapping[i + 1] = 0;
            } else {
                this._markdownLinesToEditorScrollMapping[i + 1] = Math.ceil(cummulativeEditorLinesCovered[i - 1] * lineHeight + paddingTop);
            }
        }

        this._maxEditorScrollTop = this._markdownLinesToEditorScrollMapping[cummulativeEditorLinesCovered.length];
    }

    private _createPreviewScrollToEditorScrollRangeMapping(): Map<
        { editorScrollStart: number; editorScrollEnd: number },
        { previewScrollStart: number; previewScrollEnd: number }
    > {
        const mlpElements = this._markdownContainerHtmlElement.querySelectorAll("[" + MarkdownConstants.MarkDownLinesMappingAttrName + "]");

        if (mlpElements.length === 0 || !this._markdownLinesMapping) {
            return new Map();
        }
        const initialY = this._markdownContainerHtmlElement.getBoundingClientRect().top - this._markdownContainerHtmlElement.scrollTop;
        const mappings = new Map<{ editorScrollStart: number; editorScrollEnd: number }, { previewScrollStart: number; previewScrollEnd: number }>();

        for (let i = 0; i < mlpElements.length; i++) {
            let previewScrollStart = 0;
            if (i !== 0) {
                previewScrollStart = Math.floor(mlpElements[i].getBoundingClientRect().top - initialY);
            }

            const previewScrollEnd = Math.ceil(mlpElements[i].getBoundingClientRect().bottom - initialY);
            const mlpId = mlpElements[i].getAttribute(MarkdownConstants.MarkDownLinesMappingAttrName);
            if (mlpId && this._markdownLinesMapping[mlpId].isBlock) {
                const startMarkdownLineNumber = this._markdownLinesMapping[mlpId].startLine + 1;
                const endMarkdownLineNumber = this._markdownLinesMapping[mlpId].endLine + 1;
                let editorScrollStart = this._markdownLinesToEditorScrollMapping[startMarkdownLineNumber];
                let editorScrollEnd = this._markdownLinesToEditorScrollMapping[endMarkdownLineNumber];
                if (editorScrollStart === undefined) {
                    editorScrollStart = 0;
                }
                if (editorScrollEnd === undefined) {
                    editorScrollEnd = this._maxEditorScrollTop;
                }
                mappings.set({ editorScrollStart, editorScrollEnd }, { previewScrollStart, previewScrollEnd });
            }
        }
        return mappings;
    }
}
